import React, {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {DraftProjectStatus, ProjectType} from '@contentlab/api/project/types';
import FeatureEditor from '@contentlab/components/feature-editor';
import ChooseRatios from '@contentlab/components/feature-editor/components/choose-ratios/ChooseRatios';
import {creditsTooltipTextForFeature} from '@contentlab/components/feature-editor/utils';
import {
  DEFAULT_DEBOUNCE_DELAY,
  FEATURE_BEST_PRACTICES_LOCALE_KEY,
  PROJECT_ID_SEARCH_PARAM_KEY,
} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useManageDraftProject from '@contentlab/hooks/mutations/use-manage-draft-project/useManageDraftProject';
import useGenerateProject from '@contentlab/hooks/use-generate-project/useGenerateProject';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets/useFeatureAssets';
import {useDebouncedCallback} from '@/hooks/use-debounce';

function ResizePage() {
  const {draftReformatProject} = contentLabStore(
    useShallow((state) => ({
      draftReformatProject: state.draftReformatProject,
    }))
  );

  const {uploadedVideos, assetErrors: clientAssetErrors} = useFeatureAssets(
    FeatureName.REFORMATTING
  );

  const {
    assetErrors,
    invalidAssetIds,
    validUploadedVideos,
    validClientUploadedVideos,
  } = useMergedAssetErrors({
    serverAssetErrors:
      draftReformatProject?.validationErrors?.userInput?.assets || [],
    clientAssetErrors,
    uploadedVideos,
  });

  const {uiInteraction} = useUiInteractionEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: draftProjectData,
    createDraftProject,
    updateDraftProject,
    resetDraftProject,
    isLoading: isManagingProjectLoading,
  } = useManageDraftProject({
    type: ProjectType.REFORMAT,
    draftProject: draftReformatProject,
    projectId: searchParams.get(PROJECT_ID_SEARCH_PARAM_KEY),
    onGetProjectErrorCallback: () => {
      searchParams.delete(PROJECT_ID_SEARCH_PARAM_KEY);
      setSearchParams(searchParams);
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const getProjectName = () =>
    contentLabStore.getState().draftReformatProject.projectName;

  const getReformatSpecs = (assets?: Asset[], excludeInvalidAssets = false) => {
    const aspectRatios =
      contentLabStore.getState().draftReformatProject.selectedAspectRatios;

    const assetsSource = excludeInvalidAssets
      ? (assets || readyAssets).filter(
          (asset) => !invalidAssetIds.has(asset.id)
        )
      : assets || readyAssets;

    return assetsSource.flatMap((asset) =>
      aspectRatios
        .filter((aspectRatio) => asset.metadata.aspectRatio !== aspectRatio)
        .map((aspectRatio) => ({
          assetId: asset.id,
          outputAspectRatio: aspectRatio,
        }))
    );
  };

  const createProject = async (assets?: Asset[]) => {
    if (draftReformatProject.isInitialized) {
      return;
    }

    try {
      uiInteraction({
        item_name: 'create_resize_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.RESIZE,
        flow_name: FLOW_NAMES.CONTENT_LAB.RESIZE,
        interaction_id: uuid(),
      });

      await createDraftProject({
        type: ProjectType.REFORMAT,
        projectName: getProjectName(),
        draft: {
          reformatSpecs: getReformatSpecs(assets),
        },
      });
    } catch (error) {
      // No operation
    }
  };
  const updateProject = async (
    status: DraftProjectStatus = DraftProjectStatus.UPDATE_DRAFT,
    assets?: Asset[]
  ) => {
    if (!draftReformatProject.isInitialized) {
      return;
    }

    uiInteraction({
      item_name: 'update_resize_project',
      interaction_location: CONTENT_LAB_VIEW_NAMES.RESIZE,
      flow_name: FLOW_NAMES.CONTENT_LAB.RESIZE,
      interaction_id: uuid(),
    });

    await updateDraftProject({
      status,
      projectName: getProjectName(),
      draft: {
        reformatSpecs: getReformatSpecs(
          assets,
          status === DraftProjectStatus.EXECUTE
        ),
      },
    });
  };

  const updateOrCreateProject = async (assets?: Asset[]) => {
    if (!draftReformatProject.isInitialized) {
      await createProject(assets);
    } else {
      await updateProject(DraftProjectStatus.UPDATE_DRAFT, assets);
    }
  };

  const debouncedUpdateOrCreateProject = useDebouncedCallback(
    updateOrCreateProject,
    DEFAULT_DEBOUNCE_DELAY
  );

  const readyAssets = useReadyAssets({
    assetIds: validClientUploadedVideos.map(
      (uploadedVideo) => uploadedVideo.id
    ),
    onReady: (assets) => updateOrCreateProject(assets),
    keepPreviousData: false,
  });

  // Get the common aspect ratio if all videos have the same one
  const getCommonAspectRatio = (): AspectRatio | null => {
    if (uploadedVideos.length === 0) return null;

    const firstAspectRatio = uploadedVideos[0].aspectRatio || null;
    const allSameAspectRatio = uploadedVideos.every(
      (asset) => asset.aspectRatio === firstAspectRatio
    );

    return allSameAspectRatio ? firstAspectRatio : null;
  };

  // Calculate required credits by filtering out identity transformations
  const getRequiredCredits = () =>
    validUploadedVideos.reduce((assetTokens, asset) => {
      const nonIdentityTransformations =
        draftReformatProject.selectedAspectRatios.filter(
          (targetAspectRatio: AspectRatio) =>
            asset.aspectRatio !== targetAspectRatio
        );
      const assetTokenCount = calculateVideoTokensForAssets([asset]);
      return assetTokens + assetTokenCount * nonIdentityTransformations.length;
    }, 0);

  const generateProject = useGenerateProject({
    getRequiredCredits,
    setIsSubmitting,
    draftProject: draftReformatProject,
    defaultProjectName: 'Resize',
    updateProject,
    resetProject: resetDraftProject,
  });
  const handleGenerateClick = async () => {
    setIsSubmitting(true);
    try {
      uiInteraction({
        item_name: 'execute_resize_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.RESIZE,
        flow_name: FLOW_NAMES.CONTENT_LAB.RESIZE,
        interaction_id: uuid(),
      });
      await generateProject();
    } catch (error) {
      // No operation
    }
    setIsSubmitting(false);
  };

  const isGenerateButtonDisabled = useMemo(
    () =>
      uploadedVideos.length === 0 ||
      draftReformatProject.selectedAspectRatios.length === 0 ||
      uploadedVideos.some((asset) => asset.isUploading) ||
      isSubmitting ||
      !draftReformatProject.projectName ||
      getReformatSpecs(readyAssets, true).length === 0 ||
      isManagingProjectLoading ||
      readyAssets.length !== validClientUploadedVideos.length,
    [
      uploadedVideos,
      validClientUploadedVideos,
      draftReformatProject.selectedAspectRatios,
      isSubmitting,
      draftReformatProject.projectName,
      isManagingProjectLoading,
      readyAssets,
    ]
  );

  const isGenerateButtonLoading = useMemo(
    () =>
      isSubmitting ||
      uploadedVideos.some((asset) => asset.isUploading) ||
      isManagingProjectLoading,
    [isSubmitting, uploadedVideos, isManagingProjectLoading]
  );

  const commonAspectRatio = getCommonAspectRatio();
  const activeAspectRatios = useMemo(
    () =>
      draftReformatProject.selectedAspectRatios.filter(
        (aspectRatio) => !commonAspectRatio?.includes(aspectRatio)
      ),
    [draftReformatProject.selectedAspectRatios, commonAspectRatio]
  );

  const generateProjectDescription = useMemo(() => {
    if (isSubmitting) {
      return '';
    }
    const isUploading = uploadedVideos.some((video) => video.isUploading);
    if (!isUploading && uploadedVideos.length === 0) {
      return translate('Upload videos and choose aspect ratios to generate');
    }
    if (uploadedVideos.length > 0 && !activeAspectRatios.length) {
      return translate('Choose aspect ratios to generate');
    }

    return '';
  }, [uploadedVideos, isSubmitting, activeAspectRatios]);

  return (
    <FeatureEditor
      feature={FeatureName.REFORMATTING}
      featureDetailsProps={{
        featureDetailsComponent: (
          <ChooseRatios
            disabledAspectRatio={commonAspectRatio ?? undefined}
            selected={draftReformatProject.selectedAspectRatios}
            onSelectionChange={(aspectRatios) => {
              uiInteraction({
                item_name: 'choose_aspect_ratio',
                interaction_location: CONTENT_LAB_VIEW_NAMES.RESIZE,
                flow_name: FLOW_NAMES.CONTENT_LAB.RESIZE,
                interaction_details: JSON.stringify({
                  aspect_ratio: aspectRatios.join(','),
                }),
              });
              contentLabActions.updateDraftReformatProjectAspectRatios(
                aspectRatios
              );
              debouncedUpdateOrCreateProject();
            }}
            tooltip={
              uploadedVideos.length === 1
                ? translate('Your file is already in this aspect ratio')
                : translate('Your files are already in this aspect ratio')
            }
          />
        ),
      }}
      renameProjectProps={{
        projectName: draftReformatProject.projectName,
        onBlur: (name) => {
          updateOrCreateProject();
          uiInteraction({
            item_name: 'rename_project',
            interaction_location: CONTENT_LAB_VIEW_NAMES.RESIZE,
            flow_name: FLOW_NAMES.CONTENT_LAB.RESIZE,
            interaction_details: JSON.stringify({
              project_name: name,
            }),
          });
        },
        projectNameChange: (name) => {
          contentLabActions.updateDraftReformatProjectName(name);
        },
        projectNamePlaceholder: translate('Name your Resize project'),
      }}
      generateProjectProps={{
        description: generateProjectDescription,
        assetErrors,
        readyAssets,
        onGenerateClick: handleGenerateClick,
        creditsCost: getRequiredCredits(),
        disabled: isGenerateButtonDisabled,
        isLoading: isGenerateButtonLoading,
        tooltipText: creditsTooltipTextForFeature(
          FeatureName.REFORMATTING,
          validUploadedVideos.length,
          activeAspectRatios.length
        ),
      }}
      fileUploaderProps={{
        title: translate('Upload videos'),
        guideTitle: translate('max_duration_and_size_per_file', {
          maxDuration: translate(
            getUploadLimits(FeatureName.REFORMATTING).maxDurationDisplayKey
          ),
          maxSize: translate(
            getUploadLimits(FeatureName.REFORMATTING).maxFileSizeDisplayKey
          ),
        }),
        guideDescription: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.REFORMATTING]
        ),
        shortenGuideText: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.REFORMATTING]
        ),
        readyAssets,
        assetErrors,
        onLearnMoreClick: () => {
          contentLabActions.setIsLearnMoreModalOpen(true);
          uiInteraction({
            item_name: 'learn more',
            interaction_location: CONTENT_LAB_VIEW_NAMES.RESIZE,
          });
        },
      }}
    />
  );
}

export default ResizePage;
